import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { useTestCampaignContext } from './testCampaignContext'
import AssetSelectMenu from './AssetSelectMenu'
import PreviousCampaignBanner from './PreviousCampaignBanner'
import {
  getGroupedAssets,
  validateAssetSelection,
  getAvailablePages,
  getAvailableInstagramAccounts,
  getAvailablePixels,
  isTerminalState,
} from './testCampaignUtils'
import { SecondaryButton, TransparentBgButton } from 'components/Shared/Button'

export default function AssetSelectionStep({ assets, onCancel, error }) {
  const { t } = useTranslation()
  const {
    selectedAssets,
    selectAsset,
    createCampaign,
    loading,
    currentCampaign,
    viewPreviousCampaign,
  } = useTestCampaignContext()

  const grouped = getGroupedAssets(assets)
  const isValid = validateAssetSelection(selectedAssets)

  const availablePages = useMemo(
    () => getAvailablePages(assets, selectedAssets.adAccount),
    [assets, selectedAssets.adAccount],
  )

  const availableInstagram = useMemo(
    () => getAvailableInstagramAccounts(assets, selectedAssets.adAccount, selectedAssets.page),
    [assets, selectedAssets.adAccount, selectedAssets.page],
  )

  const availablePixels = useMemo(
    () => getAvailablePixels(assets, selectedAssets.adAccount),
    [assets, selectedAssets.adAccount],
  )

  const handleSubmit = () => {
    if (isValid) {
      createCampaign()
    }
  }

  const handleAdAccountChange = (asset) => {
    selectAsset('adAccount', asset)

    const newAvailablePages = getAvailablePages(assets, asset)
    const newAvailableInstagram = getAvailableInstagramAccounts(assets, asset, selectedAssets.page)
    const newAvailablePixels = getAvailablePixels(assets, asset)

    if (
      selectedAssets.page &&
      !newAvailablePages.find((p) => p.asset_id === selectedAssets.page.asset_id)
    ) {
      selectAsset('page', null)
    }
    if (
      selectedAssets.instagram &&
      !newAvailableInstagram.find((i) => i.asset_id === selectedAssets.instagram.asset_id)
    ) {
      selectAsset('instagram', null)
    }
    if (
      selectedAssets.pixel &&
      !newAvailablePixels.find((p) => p.asset_id === selectedAssets.pixel.asset_id)
    ) {
      selectAsset('pixel', null)
    }
  }

  const handlePageChange = (asset) => {
    selectAsset('page', asset)

    const newAvailableInstagram = getAvailableInstagramAccounts(
      assets,
      selectedAssets.adAccount,
      asset,
    )

    if (
      selectedAssets.instagram &&
      !newAvailableInstagram.find((i) => i.asset_id === selectedAssets.instagram.asset_id)
    ) {
      selectAsset('instagram', null)
    }
  }

  return (
    <div className="test-campaign-asset-selection">
      <div className="test-campaign-asset-selection__content">
        <h1>
          {t('Settings.MetaAccess.TestCampaign.selectAssets', 'Select Assets for Test Campaign')}
        </h1>
        <p className="body-txt light larger mb-4">
          {t(
            'Settings.MetaAccess.TestCampaign.selectAssetsDescription',
            'Choose the assets you want to use for creating a test campaign.',
          )}
        </p>

        {isTerminalState(currentCampaign?.status) && (
          <PreviousCampaignBanner campaign={currentCampaign} onViewDetails={viewPreviousCampaign} />
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error.message ||
              t(
                'Settings.MetaAccess.TestCampaign.error.createFailed',
                'Failed to create test campaign. Please try again.',
              )}
          </div>
        )}

        <div className="d-flex flex-column gap-3">
          <AssetSelectMenu
            assets={grouped.adAccounts}
            selectedAsset={selectedAssets.adAccount}
            onSelect={handleAdAccountChange}
            label={t('Recommendations.socialAdvertAccountTitle', 'Ad Account')}
            placeholder={t('Recommendations.campaign.selectAdAccount', 'Select Ad Account')}
            required
          />

          <AssetSelectMenu
            assets={availablePages}
            selectedAsset={selectedAssets.page}
            onSelect={handlePageChange}
            label={t('Recommendations.facebookPage', 'Facebook Page')}
            placeholder={t('Recommendations.campaign.selectFacebookPage', 'Select Facebook Page')}
            required
          />

          <AssetSelectMenu
            assets={availableInstagram}
            selectedAsset={selectedAssets.instagram}
            onSelect={(asset) => selectAsset('instagram', asset)}
            onRemove={() => selectAsset('instagram', null)}
            label={t('Recommendations.instagramAccount', 'Instagram Account')}
            placeholder={t(
              'Recommendations.campaign.selectInstagramAccount',
              'Select Instagram Account',
            )}
          />

          <AssetSelectMenu
            assets={availablePixels}
            selectedAsset={selectedAssets.pixel}
            onSelect={(asset) => selectAsset('pixel', asset)}
            onRemove={() => selectAsset('pixel', null)}
            label={t('Recommendations.pixel', 'Pixel')}
            placeholder={t('Recommendations.campaign.selectPixel', 'Select Pixel')}
          />
        </div>
      </div>

      <div className="test-campaign-asset-selection__footer">
        <TransparentBgButton text={t('common.cancel', 'Cancel')} onClick={onCancel} color="navy" />
        <SecondaryButton
          className="bt"
          text={t('Settings.MetaAccess.TestCampaign.createCampaign', 'Create campaign')}
          fullWidth={false}
          onClick={handleSubmit}
          disabled={!isValid || loading}
        />
      </div>
    </div>
  )
}

AssetSelectionStep.propTypes = {
  assets: PropTypes.array.isRequired,
  onCancel: PropTypes.func.isRequired,
  error: PropTypes.object,
}
